import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthPayload, AuthService } from './authService.js';

export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated';

export interface AuthContextValue {
  status: AuthStatus;
  payload: AuthPayload | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  service: AuthService;
  children: ReactNode;
}

export function AuthProvider({ service, children }: AuthProviderProps): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [payload, setPayload] = useState<AuthPayload | null>(null);

  useEffect(() => {
    let cancelled = false;
    void service
      .verify()
      .then((result) => {
        if (cancelled) return;
        if (result) {
          setPayload(result);
          setStatus('authenticated');
        } else {
          setPayload(null);
          setStatus('unauthenticated');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setPayload(null);
        setStatus('unauthenticated');
      });
    return () => {
      cancelled = true;
    };
  }, [service]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await service.login(email, password);
      setPayload(result);
      setStatus('authenticated');
    },
    [service],
  );

  const logout = useCallback(async () => {
    try {
      await service.logout();
    } finally {
      setPayload(null);
      setStatus('unauthenticated');
    }
  }, [service]);

  return <AuthCtx.Provider value={{ status, payload, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
